import { Currency } from '@ps/web-core-enums';
import type { CasinoJackpot } from '@ps/web-martech-cms-types';

import { transformParams } from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { areGamesVisibleByConfig } from '@swf/core/models/casino/utils';
import type {
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import { pageChange } from '@swf/core/utils/actions';

const initialState: CasinoJackpotWidgetState = {
  key: null,
  winUpTo: 0,
  wonToday: 0,
  paidToday: 0,
  currency: Currency.Euro,
  gameConfigs: []
};

export interface CasinoJackpotWidgetState {
  key: string | null;
  winUpTo: number;
  wonToday: number;
  paidToday: number;
  currency: Currency;
  gameConfigs: CasinoGame[];
}

const reducers = {
  set: (state: CasinoJackpotWidgetState, payload: CasinoJackpotWidgetState) => ({
    ...state,
    ...payload
  })
};

export const effects = {
  async preload(
    payload: undefined,
    { actions, dispatch, widget: { uid } }: WidgetEffectParams<CasinoJackpot>
  ) {
    const { fetch }: ExtractDispatchFromModel<CasinoJackpotWidgetModel> = dispatch[uid];
    pageChange(actions)?.subscribe(() => fetch());

    await fetch();
  },
  async fetch(payload: undefined, params: WidgetEffectParams<CasinoJackpot>) {
    const { dispatch, select, request, rootState, widget } = params;

    const { uid } = widget;
    const widgetDispatch: ExtractDispatchFromModel<CasinoJackpotWidgetModel> = dispatch[uid];
    const widgetSelect: ExtractSelectFromModel<CasinoJackpotWidgetModel> = select[uid];
    const service = '/api/v1-preview/casino/jackpot/octoplay';
    const options = select.cms.getRequestOptions(rootState);
    const domain = select.cms.getDomain(rootState);
    const [subdomain] = domain.split('.');
    if (subdomain === 'eur') {
      options.meta.currency = Currency.Euro;
    }

    if (
      !areGamesVisibleByConfig({
        params,
        service,
        hasState: widgetSelect.get(rootState)?.gameConfigs.length > 0
      })
    ) {
      return;
    }

    try {
      const { body: data } = await request<CasinoJackpotWidgetState>({
        ...options,
        service,
        http: true,
        body: { ...transformParams(widget.params) }
      });

      widgetDispatch.set(data ? data : initialState);
    } catch (error) {
      widgetDispatch.set(initialState);
    }
  }
};

const selectors = (slice: Slicer<CasinoJackpotWidgetState>) => ({
  get() {
    return slice;
  }
});

export interface CasinoJackpotWidgetModel
  extends WidgetModelConfig<CasinoJackpot, CasinoJackpotWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const jackpotWidgetModel: CasinoJackpotWidgetModel = {
  state: initialState,
  reducers,
  effects,
  selectors
};

export const useJackpotWidgetModel: UseWidget<CasinoJackpot, CasinoJackpotWidgetModel> = useWidget;
